/**
 * 17. Financial Dashboard — cash position, AP/AR totals, bank reconciliation
 * status, period close checklist, FX gain/loss summary.
 *
 * Each figure heads in the workspace currency and lists every other currency
 * beneath it, not converted (docs/design/currency.md R5, CUR-2).
 *
 * Arabic and English (C7): every word from the catalogs; the close checks the
 * API words in English are shown in the interface's language
 * (./close-text.ts); statuses agree with the journal entry.
 */
import {
  Card,
  PageHeader,
  StatCard,
  DataTable,
  Skeleton,
  Badge,
  StatusBadge,
} from "@/components/ui";
import { MoneyStatCard, OtherCurrencies } from "@/components/Money";
import { QueryError } from "@/components/QueryError";
import { useApiQuery } from "@/lib/api/hooks";
import type { CurrencyAmount, CurrencyFigure } from "@/lib/currency";
import { useTranslation } from "@/lib/i18n";
import { formatSignedPercent } from "@/lib/i18n/format";
import { formatMoney, formatDate } from "@/lib/money";
import { closeCheckText } from "./close-text";
import { EntryNumber } from "./entry-number";
import {
  type NotAvailable,
  notAvailableFigure,
  useNotAvailableText,
} from "./not-available";

/** The cash position as a figure (none is sent until AE-ST2 builds it). */
interface CashFigure {
  balance: string;
  currency: string;
  changePct: number;
  others: CurrencyAmount[];
}

interface FinanceData {
  /**
   * REP-CASH (D115): the API sends the not-available state
   * `{status, code, capability, slice}` until the statement engine knows
   * which accounts are cash; the tile then says so and shows no number.
   */
  cashPosition: CashFigure | Record<string, unknown>;
  apTotal: CurrencyFigure;
  arTotal: CurrencyFigure;
  fxGainLoss: CurrencyFigure & { positive: boolean };
  recentEntries: Array<{
    id: string;
    /** NULL for a draft or voided entry; a number is issued only on posting. */
    entryNumber: string | null;
    entryDate: string;
    narration: string;
    status: string;
    total: string;
  }>;
  periodCloseChecklist: Array<{ item: string; done: boolean }>;
}

function isCashFigure(value: unknown): value is CashFigure {
  const v = (value ?? {}) as Record<string, unknown>;
  return (
    typeof v.balance === "string" &&
    typeof v.currency === "string" &&
    typeof v.changePct === "number" &&
    Array.isArray(v.others)
  );
}

/** The not-available sentence beneath the tile's "Not available yet". */
function NotAvailableDetail({ what }: { what: NotAvailable }) {
  const text = useNotAvailableText(what);
  return (
    <p
      role="status"
      style={{ margin: "4px 0 0", fontSize: 12, color: "var(--atlas-text)" }}
    >
      {text}
    </p>
  );
}

/**
 * The cash position tile. A figure only when the API sends one; otherwise
 * "Not available yet" and why, never a number and never a zero (REP-CASH).
 */
function CashPositionCard({ figure }: { figure: FinanceData["cashPosition"] }) {
  const { t } = useTranslation();
  const label = t("finance.dashboard.cashPosition");
  if (isCashFigure(figure))
    return (
      <StatCard
        label={label}
        value={formatMoney(figure.balance, figure.currency)}
        detail={<OtherCurrencies others={figure.others} />}
        meta={formatSignedPercent(figure.changePct)}
        tone={figure.changePct >= 0 ? "positive" : "negative"}
      />
    );
  const what = notAvailableFigure(figure);
  return (
    <StatCard
      label={label}
      value={t("finance.notAvailable.title")}
      detail={what ? <NotAvailableDetail what={what} /> : undefined}
    />
  );
}

export function FinanceDashboardPage() {
  const { t } = useTranslation();
  const { data, isPending, error, refetch, isFetching } =
    useApiQuery<FinanceData>(["finance", "dashboard"], "/finance/dashboard");

  if (isPending) return <Skeleton rows={10} />;
  if (error || !data)
    return (
      <QueryError
        error={error}
        retrying={isFetching}
        onRetry={() => void refetch()}
      />
    );

  return (
    <div>
      <PageHeader
        title={t("finance.dashboard.title")}
        subtitle={t("finance.dashboard.subtitle")}
      />

      <div className="atlas-grid atlas-grid--4 mb-4">
        <CashPositionCard figure={data.cashPosition} />
        <MoneyStatCard
          label={t("finance.dashboard.accountsPayable")}
          figure={data.apTotal}
        />
        <MoneyStatCard
          label={t("finance.dashboard.accountsReceivable")}
          figure={data.arTotal}
        />
        <MoneyStatCard
          label={t("finance.dashboard.fxGainLoss")}
          figure={data.fxGainLoss}
          tone={data.fxGainLoss.positive ? "positive" : "negative"}
        />
      </div>

      <div className="atlas-grid atlas-grid--2">
        <Card title={t("finance.dashboard.recentEntries")}>
          <DataTable
            rows={data.recentEntries}
            keyField="id"
            columns={[
              {
                key: "entryNumber",
                label: t("fields.number"),
                render: (r) => (
                  <EntryNumber value={r.entryNumber} status={r.status} />
                ),
              },
              {
                key: "entryDate",
                label: t("fields.date"),
                render: (r) => formatDate(r.entryDate),
              },
              { key: "narration", label: t("finance.dashboard.narration") },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="JournalEntry" />
                ),
              },
            ]}
          />
        </Card>
        <Card title={t("finance.dashboard.checklist")}>
          <ul style={{ margin: 0, paddingInlineStart: 18 }}>
            {data.periodCloseChecklist.map((item) => (
              <li key={item.item} style={{ marginBlockEnd: 8 }}>
                {item.done ? (
                  <Badge tone="success">✓</Badge>
                ) : (
                  <Badge tone="warning">○</Badge>
                )}{" "}
                {closeCheckText(t, item.item)}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
